import type { BasketPricingState } from '../hooks/useBasketPricing';
import type { MergedPricedLines } from '../utils/pricedLines';
import { decimalToMinorUnits } from '../utils/decimalMoney';

export type BasketGross =
    | { readonly kind: 'derived'; readonly amountMinor: number }
    | { readonly kind: 'unpriced'; readonly itemNames: readonly string[] }
    | { readonly kind: 'pending' }
    | { readonly kind: 'error' };

/** Maps the server pricing state onto the payment step's amount, a pure lookup with no
 *  arithmetic: unpriced detection is the server's per-line flag, not a client heuristic. */
export const toBasketGross = (pricing: BasketPricingState, merged: MergedPricedLines): BasketGross => {
    // Freshness outranks classification: an error or an unpriced flag read off a query
    // keyed on another basket describes lines the user is no longer looking at.
    if (!pricing.matchesInput) {
        return { kind: 'pending' };
    }
    if (pricing.error) {
        return { kind: 'error' };
    }
    if (pricing.isStale || !pricing.data || !merged.complete) {
        return { kind: 'pending' };
    }
    if (merged.unpricedNames.length > 0) {
        return { kind: 'unpriced', itemNames: merged.unpricedNames };
    }
    return { kind: 'derived', amountMinor: decimalToMinorUnits(pricing.data.totals.gross) };
};
